/**
 * DOM ownership boundaries and CSS scope roots.
 *
 * Standalone, the app owns the whole document: it may set the title and paint
 * brand CSS variables on <html>. Embedded, the page belongs to the client, so
 * both are scoped.
 *
 * "Scope roots" is plural on purpose. In embed mode the app's CSS is
 * prefix-wrapped under `.a2f-root`, and there are TWO elements carrying that
 * class: the mount container, and a body-level portal host that react-bootstrap
 * modals and overlays render into (see ./portalHost.ts). Brand variables have to
 * be written to both, or every modal loses its colours — the portal host is not
 * a descendant of the mount container, so it inherits nothing from it.
 */

import { getEmbedConfig } from "./config";

/** The CSS scope class. Must match the prefix in vite.embed.config.ts. */
export const SCOPE_CLASS = "a2f-root";

const scopeRoots = new Set<HTMLElement>();
let lastVars: Record<string, string> = {};

/** Registers an element that carries SCOPE_CLASS. Idempotent. */
export function registerScopeRoot(el: HTMLElement | null): void {
  if (!el) return;
  scopeRoots.add(el);
  // A root added later (the portal host is created on first modal) still needs
  // whatever branding has already been resolved.
  applyVarsTo(el, lastVars);
}

export function unregisterScopeRoot(el: HTMLElement | null): void {
  if (el) scopeRoots.delete(el);
}

export function clearScopeRoots(): void {
  scopeRoots.clear();
  lastVars = {};
}

/** Every element brand variables should be written to. */
function targets(): HTMLElement[] {
  if (getEmbedConfig().mode === "embed" && scopeRoots.size > 0) {
    return [...scopeRoots];
  }
  return [document.documentElement];
}

function applyVarsTo(el: HTMLElement, vars: Record<string, string>): void {
  Object.entries(vars).forEach(([name, value]) => {
    const prop = name.startsWith("--") ? name : `--${name}`;
    el.style.setProperty(prop, value);
  });
}

/**
 * Writes CSS custom properties to every scope root.
 *
 * Remembered, so a scope root registered afterwards is brought up to date.
 */
export function setScopeVars(vars: Record<string, string>): void {
  lastVars = { ...lastVars, ...vars };
  targets().forEach((el) => applyVarsTo(el, vars));
}

/**
 * Kept for callers that genuinely need one element. Prefer setScopeVars, which
 * covers the portal host too.
 */
export function getThemeTarget(): HTMLElement {
  return targets()[0] ?? document.documentElement;
}

/** Honours the manageTitle flag, so an embed does not rewrite the client's tab. */
export function setDocumentTitle(title: string): void {
  if (!title) return;
  if (!getEmbedConfig().manageTitle) return;
  document.title = title;
}

/** Applies a client's brand overrides supplied through embed config. */
export function applyThemeVars(theme: Record<string, string>): void {
  setScopeVars(theme);
}

// Back-compat with the phase 1 name; registerScopeRoot is the real API now.
export function setThemeTarget(el: HTMLElement | null): void {
  if (el) registerScopeRoot(el);
  else clearScopeRoots();
}
